import { replaceErrorStack } from "../error/shape.js";
import type { Budget } from "../interp/budget.js";
import type { ParseResult } from "../parse/parser.js";
import { DUMP_FORMAT_VERSION } from "./dump-format.js";
import { MAX_DATA_DEPTH } from "../graph-depth.js";

const DEFAULT_MAX_DEPTH = MAX_DATA_DEPTH;
const DEFAULT_MAX_ENTRIES = 100_000;
const DEFAULT_MAX_STRING_LENGTH = 1_000_000;
const DEFAULT_MAX_DATA_SIZE = 16_000_000;
const TAGGED_VALUE_KINDS = new Set([
  "array",
  "fn",
  "generator",
  "host-call-side-effect",
  "map",
  "number",
  "object",
  "promise",
  "ref",
  "regex",
  "set",
  "undefined"
]);

export type SnapshotValidationCode =
  | "budgetExceeded"
  | "danglingReference"
  | "duplicateId"
  | "invalidCycle"
  | "invalidState"
  | "invalidType"
  | "invalidValue"
  | "unknownTag"
  | "unsupportedVersion";

export class SnapshotValidationError extends Error {
  readonly code: SnapshotValidationCode;
  readonly path: string;

  constructor(code: SnapshotValidationCode, path: string, detail: string) {
    super(`Invalid snapshot at ${path}: ${detail}`);
    this.name = "SnapshotValidationError";
    this.code = code;
    this.path = path;
    replaceErrorStack(this);
  }
}

type ValidationLimits = {
  maxAggregateEntries: number;
  maxCallDepth: number;
  maxDepth: number;
  maxEntries: number;
  maxStringLength: number;
  maxDataSize: number;
};

type ValidationState = {
  allowFunctions: boolean;
  allowUndefined: boolean;
  entries: number;
  dataSize: number;
  limits: ValidationLimits;
  validateTaggedPayloads: boolean;
};

export function validateDumpEnvelope(
  snapshot: unknown
): asserts snapshot is Record<string, unknown> {
  const limits = defaultLimits();
  const root = requireRecord(snapshot, "$");
  if (root.version !== DUMP_FORMAT_VERSION) {
    fail("unsupportedVersion", "$.version", `expected ${DUMP_FORMAT_VERSION}`);
  }
  requireNonEmptyString(root.sourceHash, "$.sourceHash", limits);
  const state = {
    allowFunctions: true,
    allowUndefined: true,
    entries: 0,
    dataSize: 0,
    limits,
    validateTaggedPayloads: false
  };
  validateGenericValue(root, "$", 0, state);
  validateRunSnapshotState(root, state);
  validateDumpHeap(root, state);
}

function validateDumpHeap(root: Record<string, unknown>, state: ValidationState): void {
  const heap = root.heap === undefined ? Object.create(null) : requireRecord(root.heap, "$.heap");
  const heapIds = new Set<number>();

  for (const [key, value] of Object.entries(heap)) {
    const path = `$.heap${formatKey(key)}`;
    const id = parseHeapId(key, path);
    addUnique(heapIds, id, path);
    const entry = requireRecord(value, path);
    if (entry.kind === "array") {
      requireArray(entry.items, `${path}.items`, state);
      continue;
    }
    if (entry.kind === "object") {
      requireRecord(entry.entries, `${path}.entries`);
      continue;
    }
    fail("unknownTag", `${path}.kind`, "unknown dump heap tag");
  }

  validateDumpReferences(root, "$", 0, state, heapIds);
}

function validateDumpReferences(
  value: unknown,
  path: string,
  depth: number,
  state: ValidationState,
  heapIds: Set<number>
): void {
  if (value === null || typeof value !== "object") return;
  if (depth > state.limits.maxDepth)
    fail("budgetExceeded", path, `exceeds nesting limit ${state.limits.maxDepth}`);
  if (Array.isArray(value)) {
    value.forEach((entry, index) =>
      validateDumpReferences(entry, `${path}[${index}]`, depth + 1, state, heapIds)
    );
    return;
  }

  const record = value as Record<string, unknown>;
  if (record.kind === "ref") {
    const id = requireSafeInteger(record.id, `${path}.id`, 1);
    if (!heapIds.has(id)) fail("danglingReference", `${path}.id`, `unknown heap value ${id}`);
  }
  for (const [key, entry] of Object.entries(record)) {
    validateDumpReferences(entry, `${path}${formatKey(key)}`, depth + 1, state, heapIds);
  }
}

function validateRunSnapshotState(root: Record<string, unknown>, state: ValidationState): void {
  if (root.clock !== undefined) {
    const clock = requireRecord(root.clock, "$.clock");
    requireSafeInteger(clock.next, "$.clock.next", 0);
  }
  if (root.random !== undefined) {
    const random = requireRecord(root.random, "$.random");
    requireSafeInteger(random.seed, "$.random.seed", 0);
    requireSafeInteger(random.state, "$.random.state", 0);
  }
  if (root.loopIterations !== undefined) {
    const iterations = requireRecord(root.loopIterations, "$.loopIterations");
    for (const [nodeId, value] of Object.entries(iterations)) {
      const path = `$.loopIterations${formatKey(nodeId)}`;
      requireSafeInteger(Number(nodeId), `${path}#key`, 0);
      if (typeof value === "number") {
        requireSafeInteger(value, path, 0);
        continue;
      }
      const iteration = requireRecord(value, path);
      requireSafeInteger(iteration.index, `${path}.index`, 0);
      requireArray(iteration.values, `${path}.values`, state);
    }
  }
  if (root.pendingAwaits !== undefined) {
    const pendingAwaits = requireArray(root.pendingAwaits, "$.pendingAwaits", state);
    pendingAwaits.forEach((value, index) => {
      const path = `$.pendingAwaits[${index}]`;
      const pending = requireRecord(value, path);
      if (pending.nodeId !== undefined) requireSafeInteger(pending.nodeId, `${path}.nodeId`, 0);
      const span = requireRecord(pending.span, `${path}.span`);
      const start = validatePosition(span.start, `${path}.span.start`);
      const end = validatePosition(span.end, `${path}.span.end`);
      if (start > end) {
        fail("invalidValue", `${path}.span`, "start must not exceed end");
      }
    });
  }
}

function validatePosition(value: unknown, path: string): number {
  const position = requireRecord(value, path);
  requireSafeInteger(position.line, `${path}.line`, 1);
  requireSafeInteger(position.column, `${path}.column`, 0);
  return requireSafeInteger(position.offset, `${path}.offset`, 0);
}

export function validateInterpreterSnapshot(
  snapshot: unknown,
  nodeById: ReadonlyMap<number, ParseResult>,
  budget: Budget
): asserts snapshot is Record<string, unknown> {
  const limits = limitsFromBudget(budget);
  const state: ValidationState = {
    allowFunctions: false,
    allowUndefined: false,
    entries: 0,
    dataSize: 0,
    limits,
    validateTaggedPayloads: true
  };
  const root = requireRecord(snapshot, "$");
  requireNonEmptyString(root.sourceHash, "$.sourceHash", limits);
  requireNodeId(root.currentAstNodeId, "$.currentAstNodeId", nodeById);
  const scopes = requireArray(root.scopeChain, "$.scopeChain", state);
  const calls = requireArray(root.callStack, "$.callStack", state);
  const promises = requireArray(root.pendingPromises, "$.pendingPromises", state);
  const moduleBindings = requireRecord(root.moduleBindings, "$.moduleBindings");
  const heap = root.heap === undefined ? Object.create(null) : requireRecord(root.heap, "$.heap");

  if (calls.length > limits.maxCallDepth) {
    fail("budgetExceeded", "$.callStack", `exceeds call-depth limit ${limits.maxCallDepth}`);
  }

  const scopeIds = new Set<string | number>();
  const parentById = new Map<string | number, string | number>();
  scopes.forEach((value, index) => {
    const path = `$.scopeChain[${index}]`;
    const scope = requireRecord(value, path);
    const id = requireSnapshotId(scope.id, `${path}.id`);
    addUnique(scopeIds, id, `${path}.id`);
    if (scope.parentId !== undefined) {
      parentById.set(id, requireSnapshotId(scope.parentId, `${path}.parentId`));
    }
    validateValue(requireRecord(scope.bindings, `${path}.bindings`), `${path}.bindings`, 1, state);
  });
  for (const [id, parentId] of parentById) {
    if (!scopeIds.has(parentId))
      fail("danglingReference", scopeParentPath(scopes, id), `unknown scope ${String(parentId)}`);
  }
  validateScopeCycles(scopeIds, parentById);

  const promiseIds = new Set<string | number>();
  promises.forEach((value, index) => {
    const path = `$.pendingPromises[${index}]`;
    const promise = requireRecord(value, path);
    addUnique(promiseIds, requireSnapshotId(promise.id, `${path}.id`), `${path}.id`);
    validateValue(promise, path, 1, state);
    if (
      promise.status !== undefined &&
      !["pending", "fulfilled", "rejected"].includes(String(promise.status))
    ) {
      fail("invalidState", `${path}.status`, "unknown promise state");
    }
    if (
      promise.status === "pending" &&
      (Object.hasOwn(promise, "value") || Object.hasOwn(promise, "reason"))
    ) {
      fail("invalidState", path, "pending promise cannot contain a settled value");
    }
    if (promise.status === "fulfilled") {
      if (!Object.hasOwn(promise, "value")) {
        fail("invalidState", `${path}.value`, "fulfilled promise must contain a value");
      }
      if (Object.hasOwn(promise, "reason")) {
        fail("invalidState", path, "fulfilled promise cannot contain a rejection reason");
      }
    }
    if (promise.status === "rejected") {
      if (!Object.hasOwn(promise, "reason")) {
        fail("invalidState", `${path}.reason`, "rejected promise must contain a reason");
      }
      if (Object.hasOwn(promise, "value")) {
        fail("invalidState", path, "rejected promise cannot contain a fulfilled value");
      }
    }
    validatePendingHostCall(promise, path, state);
  });

  calls.forEach((value, index) => {
    const path = `$.callStack[${index}]`;
    const frame = requireRecord(value, path);
    requireNodeId(frame.astNodeId, `${path}.astNodeId`, nodeById);
    const scopeId = requireSnapshotId(frame.scopeId, `${path}.scopeId`);
    if (!scopeIds.has(scopeId))
      fail("danglingReference", `${path}.scopeId`, `unknown scope ${String(scopeId)}`);
    if (frame.awaitingPromiseId !== undefined) {
      const promiseId = requireSnapshotId(frame.awaitingPromiseId, `${path}.awaitingPromiseId`);
      if (!promiseIds.has(promiseId))
        fail(
          "danglingReference",
          `${path}.awaitingPromiseId`,
          `unknown promise ${String(promiseId)}`
        );
    }
  });

  for (const [key, value] of Object.entries(moduleBindings)) {
    requireNonEmptyString(key, `$.moduleBindings${formatKey(key)}`, limits);
    requireNonEmptyString(value, `$.moduleBindings${formatKey(key)}`, limits);
  }

  const heapIds = new Set<number>();
  for (const [key, value] of Object.entries(heap)) {
    const id = parseHeapId(key, `$.heap${formatKey(key)}`);
    addUnique(heapIds, id, `$.heap${formatKey(key)}`);
    validateHeapValue(value, `$.heap${formatKey(key)}`, state);
  }
  validateReferences(root, "$", 0, state, { heapIds, nodeById, promiseIds, scopeIds });
}

function validatePendingHostCall(
  promise: Record<string, unknown>,
  path: string,
  state: ValidationState
): void {
  if (promise.sideEffectTag === undefined) return;
  const tag = requireRecord(promise.sideEffectTag, `${path}.sideEffectTag`);
  if (tag.kind !== "host-call-side-effect") {
    fail("unknownTag", `${path}.sideEffectTag.kind`, "unknown host-call tag");
  }
  const callId = requireNonEmptyString(tag.callId, `${path}.sideEffectTag.callId`, state.limits);
  const moduleId = requireNonEmptyString(
    tag.moduleId,
    `${path}.sideEffectTag.moduleId`,
    state.limits
  );
  const operation = requireNonEmptyString(
    tag.operation,
    `${path}.sideEffectTag.operation`,
    state.limits
  );
  if (String(promise.id) !== callId) {
    fail("invalidState", `${path}.sideEffectTag.callId`, "must match pending promise id");
  }
  if (promise.moduleId !== moduleId) {
    fail("invalidState", `${path}.sideEffectTag.moduleId`, "must match pending module id");
  }
  if (promise.operation !== operation) {
    fail("invalidState", `${path}.sideEffectTag.operation`, "must match pending operation");
  }
}

export function validateSnapshotSourceHash(
  snapshot: unknown
): asserts snapshot is { sourceHash: string } {
  const root = requireRecord(snapshot, "$");
  requireNonEmptyString(root.sourceHash, "$.sourceHash", defaultLimits());
}

function validateReferences(
  value: unknown,
  path: string,
  depth: number,
  state: ValidationState,
  refs: {
    heapIds: Set<number>;
    nodeById: ReadonlyMap<number, ParseResult>;
    promiseIds: Set<string | number>;
    scopeIds: Set<string | number>;
  }
): void {
  if (value === null || typeof value !== "object") return;
  if (depth > state.limits.maxDepth)
    fail("budgetExceeded", path, `exceeds nesting limit ${state.limits.maxDepth}`);
  if (Array.isArray(value)) {
    value.forEach((entry, index) =>
      validateReferences(entry, `${path}[${index}]`, depth + 1, state, refs)
    );
    return;
  }
  const record = value as Record<string, unknown>;
  if (typeof record.kind === "string") {
    if (record.kind === "ref") {
      const id = requireSafeInteger(record.id, `${path}.id`, 1);
      if (!refs.heapIds.has(id))
        fail("danglingReference", `${path}.id`, `unknown heap value ${id}`);
    } else if (record.kind === "promise") {
      const id = requireSnapshotId(record.id, `${path}.id`);
      if (!refs.promiseIds.has(id))
        fail("danglingReference", `${path}.id`, `unknown promise ${String(id)}`);
    } else if (record.kind === "fn" || record.kind === "generator") {
      if (record.state !== "done") {
        requireNodeId(record.astNodeId, `${path}.astNodeId`, refs.nodeById);
        const scopeId = requireSnapshotId(record.capturedScopeId, `${path}.capturedScopeId`);
        if (!refs.scopeIds.has(scopeId))
          fail("danglingReference", `${path}.capturedScopeId`, `unknown scope ${String(scopeId)}`);
      }
      if (record.kind === "generator") validateGenerator(record, path, refs.nodeById);
    }
  }
  for (const [key, entry] of Object.entries(record))
    validateReferences(entry, `${path}${formatKey(key)}`, depth + 1, state, refs);
}

function validateValue(value: unknown, path: string, depth: number, state: ValidationState): void {
  validateGenericValue(value, path, depth, state);
}

function validateTaggedValue(
  record: Record<string, unknown>,
  path: string,
  state: ValidationState
): void {
  switch (record.kind) {
    case "undefined":
      return;
    case "number":
      if (!["-Infinity", "Infinity", "NaN"].includes(String(record.value))) {
        fail("invalidValue", `${path}.value`, "unknown non-finite number value");
      }
      return;
    case "fn":
      requireSafeInteger(record.astNodeId, `${path}.astNodeId`, 0);
      requireSnapshotId(record.capturedScopeId, `${path}.capturedScopeId`);
      return;
    case "generator":
      validateGeneratorShape(record, path, state);
      return;
    case "promise":
      requireSnapshotId(record.id, `${path}.id`);
      return;
    case "ref":
      requireSafeInteger(record.id, `${path}.id`, 1);
      return;
    case "regex":
      requireString(record.source, `${path}.source`, state.limits);
      requireString(record.flags, `${path}.flags`, state.limits);
      requireSafeInteger(record.lastIndex, `${path}.lastIndex`, 0);
      return;
    case "array":
    case "object":
    case "map":
    case "set":
      return;
  }
}

function validateGeneratorShape(
  record: Record<string, unknown>,
  path: string,
  state: ValidationState
): void {
  if (!["start", "suspended", "done"].includes(String(record.state))) {
    fail("invalidState", `${path}.state`, "unknown generator state");
  }
  if (record.state === "done") {
    if (
      record.astNodeId !== undefined ||
      record.capturedScopeId !== undefined ||
      record.sent !== undefined ||
      record.yieldNodeId !== undefined
    ) {
      fail("invalidState", path, "done generator cannot contain resume state");
    }
    return;
  }

  requireSafeInteger(record.astNodeId, `${path}.astNodeId`, 0);
  requireSnapshotId(record.capturedScopeId, `${path}.capturedScopeId`);
  if (record.state === "start") {
    if (record.sent !== undefined || record.yieldNodeId !== undefined) {
      fail("invalidState", path, "unstarted generator cannot contain a resume cursor");
    }
    return;
  }

  requireSafeInteger(record.yieldNodeId, `${path}.yieldNodeId`, 0);
  const sent = requireArray(record.sent, `${path}.sent`, state);
  sent.forEach((completion, index) => {
    const completionPath = `${path}.sent[${index}]`;
    const candidate = requireRecord(completion, completionPath);
    if (!Object.hasOwn(candidate, "type")) {
      fail("invalidValue", `${completionPath}.type`, "completion type is required");
    }
    if (!["normal", "return", "throw"].includes(String(candidate.type))) {
      fail("invalidState", `${completionPath}.type`, "unknown generator completion type");
    }
    if (!Object.hasOwn(candidate, "value")) {
      fail("invalidValue", `${completionPath}.value`, "completion value is required");
    }
  });
}

function validateHeapValue(value: unknown, path: string, state: ValidationState): void {
  const record = requireRecord(value, path);
  if (!["array", "object", "map", "set"].includes(String(record.kind)))
    fail("unknownTag", `${path}.kind`, "unknown heap tag");
  validateValue(record, path, 1, state);
  if (record.kind === "array") requireArray(record.items, `${path}.items`, state);
  if (record.kind === "object") requireRecord(record.entries, `${path}.entries`);
  if (record.kind === "map") {
    const entries = requireArray(record.entries, `${path}.entries`, state);
    entries.forEach((entry, index) => {
      if (!Array.isArray(entry) || entry.length !== 2)
        fail("invalidValue", `${path}.entries[${index}]`, "map entry must contain two values");
    });
  }
  if (record.kind === "set") requireArray(record.values, `${path}.values`, state);
}

function validateGenerator(
  record: Record<string, unknown>,
  path: string,
  nodeById: ReadonlyMap<number, ParseResult>
): void {
  if (record.state === "suspended") {
    requireNodeId(record.yieldNodeId, `${path}.yieldNodeId`, nodeById);
  }
}

function validateGenericValue(
  value: unknown,
  path: string,
  depth: number,
  state: ValidationState
): void {
  if (depth > state.limits.maxDepth)
    fail("budgetExceeded", path, `exceeds nesting limit ${state.limits.maxDepth}`);
  state.entries += 1;
  if (state.entries > state.limits.maxAggregateEntries)
    fail(
      "budgetExceeded",
      path,
      `exceeds aggregate entry limit ${state.limits.maxAggregateEntries}`
    );
  if (typeof value === "string") {
    requireString(value, path, state.limits);
    state.dataSize += value.length;
  } else if (typeof value === "number") {
    if (Number.isInteger(value) && !Number.isSafeInteger(value))
      fail("invalidValue", path, "integer must be safe");
  } else if (Array.isArray(value)) {
    if (value.length > state.limits.maxEntries) fail("budgetExceeded", path, "array is too large");
    value.forEach((entry, index) =>
      validateGenericValue(entry, `${path}[${index}]`, depth + 1, state)
    );
  } else if (value !== null && typeof value === "object") {
    const record = value as Record<string, unknown>;
    if (isTaggedValueShape(record)) {
      if (state.validateTaggedPayloads) validateTaggedValue(record, path, state);
    }
    for (const [key, entry] of Object.entries(value)) {
      requireString(key, `${path}${formatKey(key)}`, state.limits);
      state.dataSize += key.length;
      validateGenericValue(entry, `${path}${formatKey(key)}`, depth + 1, state);
    }
  } else if (
    !["boolean", "number"].includes(typeof value) &&
    !(state.allowFunctions && typeof value === "function") &&
    !(state.allowUndefined && value === undefined) &&
    value !== null
  ) {
    fail("invalidType", path, "contains an unsupported value");
  }
  if (state.dataSize > state.limits.maxDataSize)
    fail("budgetExceeded", path, `exceeds aggregate data limit ${state.limits.maxDataSize}`);
}

function validateScopeCycles(
  ids: Set<string | number>,
  parentById: Map<string | number, string | number>
): void {
  for (const id of ids) {
    const seen = new Set<string | number>();
    let current: string | number | undefined = id;
    while (current !== undefined) {
      if (seen.has(current))
        fail("invalidCycle", "$.scopeChain", `scope parent cycle includes ${String(current)}`);
      seen.add(current);
      current = parentById.get(current);
    }
  }
}

function limitsFromBudget(budget: Budget): ValidationLimits {
  return {
    maxAggregateEntries: DEFAULT_MAX_ENTRIES,
    maxCallDepth: budget.limits.maxCallDepth ?? 10_000,
    maxDepth: Math.min(DEFAULT_MAX_DEPTH, budget.limits.maxCallDepth ?? DEFAULT_MAX_DEPTH),
    maxEntries: budget.limits.arrayLength ?? DEFAULT_MAX_ENTRIES,
    maxStringLength: budget.limits.stringLength ?? DEFAULT_MAX_STRING_LENGTH,
    maxDataSize: budget.limits.dataSize ?? DEFAULT_MAX_DATA_SIZE
  };
}

function defaultLimits(): ValidationLimits {
  return {
    maxAggregateEntries: DEFAULT_MAX_ENTRIES,
    maxCallDepth: 10_000,
    maxDepth: DEFAULT_MAX_DEPTH,
    maxEntries: DEFAULT_MAX_ENTRIES,
    maxStringLength: DEFAULT_MAX_STRING_LENGTH,
    maxDataSize: DEFAULT_MAX_DATA_SIZE
  };
}

function requireArray(value: unknown, path: string, state: ValidationState): unknown[] {
  if (!Array.isArray(value)) fail("invalidType", path, "must be an array");
  if (value.length > state.limits.maxEntries)
    fail("budgetExceeded", path, `exceeds collection entry limit ${state.limits.maxEntries}`);
  return value;
}

function requireRecord(value: unknown, path: string): Record<string, unknown> {
  if (typeof value !== "object" || value === null || Array.isArray(value))
    fail("invalidType", path, "must be an object");
  return value as Record<string, unknown>;
}

function requireNodeId(
  value: unknown,
  path: string,
  nodeById: ReadonlyMap<number, ParseResult>
): number {
  const id = requireSafeInteger(value, path, 0);
  if (!nodeById.has(id)) fail("danglingReference", path, `unknown AST node ${id}`);
  return id;
}

function requireSnapshotId(value: unknown, path: string): string | number {
  if (typeof value === "string") {
    if (value.length === 0) fail("invalidValue", path, "id must not be empty");
    return value;
  }
  return requireSafeInteger(value, path, 0);
}

function requireSafeInteger(value: unknown, path: string, minimum: number): number {
  if (typeof value !== "number" || !Number.isSafeInteger(value) || value < minimum)
    fail("invalidValue", path, `must be a safe integer >= ${minimum}`);
  return value;
}

function requireNonEmptyString(value: unknown, path: string, limits: ValidationLimits): string {
  const text = requireString(value, path, limits);
  if (text.length === 0) fail("invalidValue", path, "must not be empty");
  return text;
}

function requireString(value: unknown, path: string, limits: ValidationLimits): string {
  if (typeof value !== "string") fail("invalidType", path, "must be a string");
  if (value.length > limits.maxStringLength)
    fail("budgetExceeded", path, `exceeds string limit ${limits.maxStringLength}`);
  return value;
}

function addUnique<T>(values: Set<T>, value: T, path: string): void {
  if (values.has(value)) fail("duplicateId", path, `duplicate id ${String(value)}`);
  values.add(value);
}

function parseHeapId(value: string, path: string): number {
  if (String(Number(value)) !== value)
    fail("invalidValue", path, "heap key must be a canonical positive integer");
  return requireSafeInteger(Number(value), path, 1);
}

function scopeParentPath(scopes: unknown[], id: string | number): string {
  const index = scopes.findIndex(
    (entry) =>
      typeof entry === "object" &&
      entry !== null &&
      !Array.isArray(entry) &&
      (entry as Record<string, unknown>).id === id
  );
  return `$.scopeChain[${index}].parentId`;
}

function formatKey(key: string): string {
  return /^[A-Za-z_$][A-Za-z0-9_$]*$/.test(key) ? `.${key}` : `[${JSON.stringify(key)}]`;
}

function isTaggedValueShape(value: Record<string, unknown>): boolean {
  return typeof value.kind === "string" && TAGGED_VALUE_KINDS.has(value.kind);
}

function fail(code: SnapshotValidationCode, path: string, detail: string): never {
  throw new SnapshotValidationError(code, path, detail);
}
